import React, { useEffect, useMemo, useState } from 'react';
import {
  Users,
  Eye,
  MessageCircle,
  Send,
  TrendingDown,
  RefreshCw,
  Loader,
  ExternalLink,
  Calendar,
} from 'lucide-react';
import {
  fetchKpiSummary,
  fetchFunnel,
  fetchVisitorsList,
  fetchVisitorTimeline,
  type KpiSummary,
  type FunnelData,
  type VisitorList,
  type VisitorRow,
  type Timeline,
} from '@/modules/public-tracking/api';

interface Props {
  addNotification?: (n: { type: string; title: string; message?: string }) => void;
}

const DAY_OPTIONS = [1, 7, 30, 90];

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export const VisitorsPage: React.FC<Props> = ({ addNotification }) => {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [kpi, setKpi] = useState<KpiSummary | null>(null);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [visitors, setVisitors] = useState<VisitorList | null>(null);
  const [selectedVisitor, setSelectedVisitor] = useState<VisitorRow | null>(null);
  const [timeline, setTimeline] = useState<Timeline | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [k, f, v] = await Promise.all([
        fetchKpiSummary(days),
        fetchFunnel(days),
        fetchVisitorsList(days, 100),
      ]);
      setKpi(k);
      setFunnel(f);
      setVisitors(v);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'Error cargando KPIs',
        message: e?.message || 'unknown',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const openTimeline = async (v: VisitorRow) => {
    setSelectedVisitor(v);
    setTimeline(null);
    try {
      const t = await fetchVisitorTimeline(v.visitor_id, 200);
      setTimeline(t);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'Error cargando timeline',
        message: e?.message,
      });
    }
  };

  const sparkMax = useMemo(() => {
    if (!kpi?.per_day?.length) return 1;
    return Math.max(...kpi.per_day.map((p) => Math.max(p.visitors, p.pageviews)), 1);
  }, [kpi]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="h-6 w-6 text-[var(--acc1)]" />
              Visitantes públicos · KPIs del funnel
            </h2>
            <p className="text-white/60 text-sm mt-1">
              Visitas únicas, pageviews, abandono, conversión — multi-tenant por org_id (JWT).
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Calendar className="h-4 w-4 text-white/50" />
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  days === d
                    ? 'bg-[var(--acc1)] text-white'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={load}
              disabled={loading}
              className="ml-2 p-2 hover:bg-white/10 rounded-lg disabled:opacity-50"
              title="Recargar"
            >
              <RefreshCw className={`h-4 w-4 text-white/70 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={<Users className="h-5 w-5" />}
            label="Visitas únicas"
            value={kpi.visitors_unique}
            color="emerald"
          />
          <KpiCard
            icon={<Eye className="h-5 w-5" />}
            label="Pageviews"
            value={kpi.pageviews}
            color="cyan"
          />
          <KpiCard
            icon={<MessageCircle className="h-5 w-5" />}
            label="Chat abierto"
            value={kpi.chat_opens}
            sub={`${pct(kpi.chat_open_rate)} de visitas`}
            color="fuchsia"
          />
          <KpiCard
            icon={<Send className="h-5 w-5" />}
            label="Form submits"
            value={kpi.form_submits}
            sub={`${pct(kpi.form_completion_rate)} completion`}
            color="amber"
          />
        </div>
      )}

      {/* Per-day sparkline */}
      {kpi?.per_day && kpi.per_day.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-3">
            Visitas únicas + Pageviews · últimos {days}d
          </h3>
          <div className="flex items-end gap-1 h-32">
            {kpi.per_day.map((p) => (
              <div
                key={p.date}
                className="flex-1 flex flex-col items-center gap-0.5 group"
                title={`${p.date}: ${p.visitors} visitors, ${p.pageviews} pageviews`}
              >
                <div className="w-full flex gap-px items-end h-full">
                  <div
                    className="flex-1 bg-emerald-500/70 group-hover:bg-emerald-400 rounded-sm"
                    style={{ height: `${(p.visitors / sparkMax) * 100}%`, minHeight: 2 }}
                  />
                  <div
                    className="flex-1 bg-cyan-500/70 group-hover:bg-cyan-400 rounded-sm"
                    style={{ height: `${(p.pageviews / sparkMax) * 100}%`, minHeight: 2 }}
                  />
                </div>
                <span className="text-[9px] text-white/40">{p.date.slice(5)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs text-white/60 mt-3">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 bg-emerald-500/70 rounded-sm" />
              Visitas únicas
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 bg-cyan-500/70 rounded-sm" />
              Pageviews
            </span>
          </div>
        </div>
      )}

      {/* Funnel + Top paths */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {funnel && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <TrendingDown className="h-4 w-4" /> Funnel · drop-off por etapa
            </h3>
            <div className="space-y-2">
              {funnel.steps.map((s, i) => {
                const widthPct = Math.max(s.rate_from_top * 100, 2);
                return (
                  <div key={s.step} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/80">{i + 1}. {s.step}</span>
                      <span className="text-white/60 text-xs">
                        {s.count.toLocaleString()} · {pct(s.rate_from_top)}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-white/5 rounded">
                      <div
                        className="h-2 bg-gradient-to-r from-emerald-500 via-cyan-500 to-fuchsia-500 rounded transition-all"
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {kpi?.top_paths && kpi.top_paths.length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Eye className="h-4 w-4" /> Top pages
            </h3>
            <div className="space-y-1">
              {kpi.top_paths.map((p) => (
                <div
                  key={p.path}
                  className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0"
                >
                  <code className="text-white/80 truncate text-xs">{p.path || '/'}</code>
                  <span className="text-white/60 text-xs">{p.pageviews}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Visitors list */}
      {visitors && visitors.visitors.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-3">
            Visitantes recientes ({visitors.total} total)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/50 text-xs border-b border-white/10">
                  <th className="py-2 pr-3">Visitor ID</th>
                  <th className="py-2 pr-3">Last path</th>
                  <th className="py-2 pr-3">Events</th>
                  <th className="py-2 pr-3">First seen</th>
                  <th className="py-2 pr-3">Last seen</th>
                  <th className="py-2 pr-3">Locale</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {visitors.visitors.map((v) => (
                  <tr key={v.visitor_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 pr-3">
                      <code className="text-[var(--acc1)] text-xs">
                        {v.visitor_id.slice(0, 8)}…
                      </code>
                      {v.has_identity && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-300 rounded">
                          identified
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-white/70 text-xs">
                      {v.last_path || '/'}
                    </td>
                    <td className="py-2 pr-3 text-white/80">{v.events_count}</td>
                    <td className="py-2 pr-3 text-white/50 text-xs">{formatDate(v.first_seen)}</td>
                    <td className="py-2 pr-3 text-white/50 text-xs">{formatDate(v.last_seen)}</td>
                    <td className="py-2 pr-3 text-white/50 text-xs">
                      {v.locale || '-'}
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        onClick={() => openTimeline(v)}
                        className="p-1 hover:bg-white/10 rounded"
                        title="Ver timeline"
                      >
                        <ExternalLink className="h-4 w-4 text-white/60" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visitors && visitors.visitors.length === 0 && !loading && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
          <Users className="h-10 w-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/60">
            Aún no hay visitantes registrados en este periodo.
          </p>
          <p className="text-white/40 text-xs mt-2">
            Asegúrate de que el frontend tenga TrackingHooks montado y env.API_URL configurado.
          </p>
        </div>
      )}

      {/* Timeline modal */}
      {selectedVisitor && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVisitor(null)}
        >
          <div
            className="bg-[var(--bg-2)] border border-white/10 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-white/10 p-4 flex items-center justify-between">
              <h3 className="text-white font-semibold">
                Timeline · <code className="text-[var(--acc1)] text-sm">{selectedVisitor.visitor_id.slice(0, 12)}…</code>
              </h3>
              <button
                onClick={() => setSelectedVisitor(null)}
                className="p-1 hover:bg-white/10 rounded text-white/60"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {!timeline && (
                <div className="text-white/50 flex items-center gap-2">
                  <Loader className="h-4 w-4 animate-spin" /> cargando…
                </div>
              )}
              {timeline?.events.map((ev, i) => (
                <div
                  key={i}
                  className="border border-white/10 rounded p-2 text-xs flex items-start gap-3"
                >
                  <span className="text-white/40 whitespace-nowrap">{formatDate(ev.ts)}</span>
                  <span className="text-[var(--acc1)] font-medium whitespace-nowrap">
                    {ev.event_type}
                  </span>
                  {ev.path && <span className="text-white/60">{ev.path}</span>}
                  {Object.keys(ev.props || {}).length > 0 && (
                    <code className="text-white/40 text-[10px] truncate">
                      {JSON.stringify(ev.props)}
                    </code>
                  )}
                </div>
              ))}
              {timeline?.events.length === 0 && (
                <p className="text-white/50">Sin eventos.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Subcomponente KPI Card ──────────────────────────────────────

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub?: string;
  color: 'emerald' | 'cyan' | 'fuchsia' | 'amber';
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, sub, color }) => {
  const colorMap = {
    emerald: 'text-emerald-300 bg-emerald-500/10',
    cyan: 'text-cyan-300 bg-cyan-500/10',
    fuchsia: 'text-fuchsia-300 bg-fuchsia-500/10',
    amber: 'text-amber-300 bg-amber-500/10',
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorMap[color]}`}>{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-2xl font-bold leading-tight">
            {value.toLocaleString()}
          </div>
          <div className="text-white/60 text-xs">{label}</div>
          {sub && <div className="text-white/40 text-[10px] mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
};
